import { Component, EventEmitter, Input, Output, SimpleChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { NgbDateParserFormatter, NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { isEqual } from 'lodash-es'
import { Subject, Subscription, debounceTime } from 'rxjs'
import { LocaleAwareNgbDateParserFormatter } from '../../vendors/ngb.vendor.module'

const isInputChangingKey: (key:string) => boolean 
  = (key) => [ '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'Delete' ].includes(key)
@Component({
    selector: 'app-datetime-input',
    templateUrl: './datetime-input.component.html',
    providers: [
        { provide: NgbDateParserFormatter, useClass: LocaleAwareNgbDateParserFormatter }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatetimeInputComponent implements OnDestroy {
  @Input() readonly = false

  @Input() displayLabel = true
  @Input() timezone: string
  @Input() dateFormat: string

  _minDateAsNgb: NgbDateStruct | undefined
  @Input() minDate: Date | undefined

  _maxDateAsNgb: NgbDateStruct | undefined
  @Input() maxDate: Date | undefined

  @Input() timeStep = 10

  @Input() zonedDate: Date | null | undefined

  zonedDateForm = new FormGroup({
    date: new FormControl<NgbDateStruct>({ year: NaN, month: NaN, day: NaN, }, { nonNullable: true }),
    time: new FormControl<NgbTimeStruct>({ hour: NaN, minute: NaN, second: NaN, }, { nonNullable: true })
  })

  @Output() zonedDateChange = new EventEmitter<Date>();

  private timePickerInputTouched$ = new Subject<HTMLInputElement>()

  subscriptions: Subscription[] = [
    this.zonedDateForm.valueChanges.subscribe(value => {
      if(this.timezone === undefined) throw Error('timezone is undefined')
      if(!value) return
      const zonedDate: Date
        = value.date && !isNaN(value.date.year) && !isNaN(value.date.month) && !isNaN(value.date.day)
        && value.time && !isNaN(value.time.hour) && !isNaN(value.time.minute)
          ? zonedTimeToUtc(
            `${value.date.year}-${value.date.month.toString().padStart(2, '0')}-${value.date.day.toString().padStart(2, '0')}T${value.time.hour.toString().padStart(2, '0')}:${(value.time.minute - (value.time.minute % this.timeStep)).toString().padStart(2, '0')}:00.000`,
            this.timezone
          )
          : new Date('invalid date')
      if(this.zonedDate?.valueOf() !== zonedDate.valueOf()) {
        this.zonedDateChange.emit(zonedDate)
      }
    }),
    this.timePickerInputTouched$.pipe(
      debounceTime(400)
    ).subscribe((inputElement)=>{
      // simulate blur event so that nbg timepicker apply its on blur handler (ie coerce value to a valid time and update forme control vaue)
      // only do it if the target element is the active element to avoid refocusing an element that already has lost focus by other means during the debounce delay
      if (inputElement === document.activeElement){
        inputElement.blur()
        inputElement.focus()
      }
    })
  ]
  ngOnDestroy (): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }
  ngOnChanges (changes: SimpleChanges): void {
    if(!this.timezone) throw Error('timezone undefined')
    if(changes.minDate || changes.maxDate){
      this.rangeDateUpdate()
    }
    if(changes.zonedDate) {
      const date = changes.zonedDate.currentValue
      if(!this.timezone) throw Error('timezone undefined')
      if(date === undefined || date === null) {
        this.zonedDateForm.setValue({
          date: { year: NaN, month: NaN, day: NaN, },
          time: { hour: NaN, minute: NaN, second: NaN, }
        }, { emitEvent: false })
      } else if(isNaN(date.valueOf())) {
        return
      } else {
        const zonedDate = utcToZonedTime(date.valueOf(), this.timezone)
        const formValueFromInputs = {
          date: { year: zonedDate.getFullYear(), month: zonedDate.getMonth() + 1, day: zonedDate.getDate(), },
          time: { hour: zonedDate.getHours(), minute: zonedDate.getMinutes(), second: 0, }
        }
        if(!isEqual(this.zonedDateForm.getRawValue(), formValueFromInputs)) {
          this.zonedDateForm.setValue(formValueFromInputs, { emitEvent: false })
        }
      }
    }
    if(changes.timeStep && !(Number.isInteger(changes.timeStep.currentValue) && changes.timeStep.currentValue <= 60)) throw `invalid timeStep ${changes.timeStep.currentValue}, expected integer between 0 and 60`
  }

  private rangeDateUpdate () {
    if (this.minDate === undefined || this.minDate === null || isNaN(this.minDate.valueOf())) {
      this._minDateAsNgb = {
        year : NaN,
        month: NaN,
        day  : NaN,
      }
    } else {
      const zonedMinDate = utcToZonedTime(this.minDate.valueOf(), this.timezone)
      this._minDateAsNgb = {
        year : zonedMinDate.getFullYear(),
        month: zonedMinDate.getMonth() + 1,
        day  : zonedMinDate.getDate(),
      }
    }
    if (this.maxDate === undefined || this.maxDate === null || isNaN(this.maxDate.valueOf())) {
      this._maxDateAsNgb = {
        year : NaN,
        month: NaN,
        day  : NaN,
      }
    } else {
      const zonedMaxDate = utcToZonedTime(this.maxDate.valueOf(), this.timezone)
      this._maxDateAsNgb = {
        year : zonedMaxDate.getFullYear(),
        month: zonedMaxDate.getMonth() + 1,
        day  : zonedMaxDate.getDate(),
      }
    }
  }

  handleTimePickerKeyUpEvent (event): void{
    if (!(event instanceof KeyboardEvent && event.type === 'keyup')){
      throw Error('this action is expected to be trigged by keyup events only')
    }
    if (!(event.target instanceof HTMLInputElement)){
      throw Error('this action is expected to be trigged by event withinput element target only')
    }
    if (isInputChangingKey(event.key)){
      this.timePickerInputTouched$.next(event.target)
    }
  }

  setDateToNow (): void {
    if(this.timezone === undefined) throw Error('timezone is undefined')
    const now = utcToZonedTime(Date.now(), this.timezone)
    this.zonedDateForm.setValue({
      date: {
        year : now.getFullYear(),
        month: now.getMonth() + 1,
        day  : now.getDate(),
      },
      time: {
        hour  : now.getHours(),
        minute: now.getMinutes() - (now.getMinutes() % this.timeStep),
        second: 0,
      }
    })
  }
}
